import type React from "react";

export const inputCls =
  "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500";
export const labelCls = "block text-xs font-medium text-gray-400 mb-1";
export const btnPrimary =
  "px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 disabled:opacity-50";
export const btnGhost = "px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-gray-800";
export const cardCls = "bg-gray-900 border border-gray-800 rounded-2xl p-5";
export const helpCls = "text-gray-500 text-xs mt-1";

export function Help({ children }: { children: React.ReactNode }) {
  return <p className={helpCls}>{children}</p>;
}

export function HowItWorks({ children }: { children: React.ReactNode }) {
  return (
    <details className="bg-gray-800/50 border border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-400">
      <summary className="cursor-pointer text-gray-300 font-medium select-none">How this works</summary>
      <div className="mt-2 space-y-1 leading-relaxed">{children}</div>
    </details>
  );
}

export function errMsg(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => (d as { msg?: string })?.msg ?? "").filter(Boolean).join("; ") || fallback;
  }
  return err?.message || fallback;
}

export function ModalShell({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`bg-gray-900 border border-gray-800 rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none" aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}


/** A person by name + email (never a raw id). `fallback` is shown when the user no longer exists. */
export function PersonCell({
  person,
  fallback = "Unknown user",
}: {
  person?: { name?: string | null; email?: string | null } | null;
  fallback?: string;
}) {
  const name = person?.name?.trim();
  const email = person?.email?.trim();
  if (!name && !email) return <span className="text-gray-500">{fallback}</span>;
  return (
    <span className="inline-block min-w-0">
      <span className="block text-white truncate">{name || email}</span>
      {name && email && <span className="block text-xs text-gray-500 break-all">{email}</span>}
    </span>
  );
}

/** Inline load failure with a retry (instead of a silent empty list). */
export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3 py-3" role="alert">
      <p className="text-sm text-red-400">{message}</p>
      <button onClick={onRetry} className={btnPrimary}>Retry</button>
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <p className="text-gray-300 text-sm font-medium">{title}</p>
      {hint && <p className="text-gray-500 text-xs max-w-md">{hint}</p>}
      {action}
    </div>
  );
}

export function StatusPill({ tone, children, title }: { tone: "green" | "blue" | "gray" | "red" | "amber"; children: React.ReactNode; title?: string }) {
  const cls = {
    green: "bg-emerald-900/60 text-emerald-300",
    blue: "bg-blue-900/60 text-blue-300",
    gray: "bg-gray-700 text-gray-300",
    red: "bg-red-900/50 text-red-300",
    amber: "bg-amber-900/50 text-amber-300",
  }[tone];
  return <span title={title} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{children}</span>;
}

/** Section card with an anchor id, a title, a one-line summary and a right-side action. */
export function SectionCard({
  id, title, summary, description, action, children,
}: {
  id: string;
  title: string;
  summary?: React.ReactNode;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className={`${cardCls} scroll-mt-20`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 id={`${id}-title`} className="text-white font-semibold">{title}</h2>
            {summary}
          </div>
          <p className="text-gray-500 text-xs">{description}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
